import React, { useState, useEffect } from 'react';
import {
  CompanyMemory,
  useCompanyMemory,
  useUpdateCompanyMemory,
} from '../../../../api/company-memory';
import { Feedback } from '@plerionhq/design-system';
import { LoadingState } from '../../../common/LoadingState';
import { Button } from '../../../onboarding/components';

import { useTranslation } from 'react-i18next';

// URL validation helper
const isValidUrl = (urlString: string): boolean => {
  if (!urlString || urlString.trim() === '') {
    return true; // Empty is valid (optional field)
  }

  try {
    // Try to create a URL object - this validates the format
    const url = new URL(urlString);
    // Check if it's http or https
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    // If URL constructor throws, it's invalid
    return false;
  }
};

export const TenantMemoryComponent: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const { data: companyMemoryResponse, isLoading, error } = useCompanyMemory();
  const updateCompanyMemoryMutation = useUpdateCompanyMemory();
  const [localCompanyMemory, setLocalCompanyMemory] = useState<
    Record<string, string | undefined>
  >({});

  const [urlError, setUrlError] = useState<string | null>(null);

  // Update local state when data is loaded (otherNotes: string[] -> display as newline-joined string)
  useEffect(() => {
    if (companyMemoryResponse) {
      setLocalCompanyMemory({
        companyName: companyMemoryResponse.companyName || '',
        website: companyMemoryResponse.website || '',
        otherNotes: companyMemoryResponse.otherNotes?.join('\n') ?? '',
      });
    } else {
      // Initialize with empty values if no data exists
      setLocalCompanyMemory({
        companyName: '',
        website: '',
        otherNotes: '',
      });
    }
  }, [companyMemoryResponse]);

  // Validate URL when website field changes
  useEffect(() => {
    const website = localCompanyMemory.website || '';
    if (website.trim() === '') {
      setUrlError(null);
      return;
    }

    if (!isValidUrl(website)) {
      setUrlError(t('pleaseEnterAValidUrlEG'));
    } else {
      setUrlError(null);
    }
  }, [localCompanyMemory.website, t]);

  const handleFieldChange = (
    field: keyof Omit<CompanyMemory, 'updatedAt' | 'createdAt'>,
    value: string,
  ) => {
    const updatedMemory = { ...localCompanyMemory, [field]: value };
    setLocalCompanyMemory(updatedMemory);
  };

  const handleSave = async () => {
    // Validate URL before saving
    const website = localCompanyMemory.website || '';
    if (website.trim() && !isValidUrl(website)) {
      setUrlError(t('pleaseEnterAValidUrlEG'));
      return;
    }

    const otherNotesRaw = localCompanyMemory.otherNotes ?? '';
    const otherNotesArray = otherNotesRaw
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    await updateCompanyMemoryMutation.mutateAsync({
      companyName: localCompanyMemory.companyName || undefined,
      website: localCompanyMemory.website || undefined,
      otherNotes: otherNotesArray.length > 0 ? otherNotesArray : undefined,
    });
  };

  const questionLabelClass =
    'mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300';
  const inputClass =
    'w-full rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-orange-400 focus:ring-1 focus:ring-orange-400';
  const textareaClass = `${inputClass} mt-2 min-h-[100px] resize-y`;

  if (isLoading) {
    return <LoadingState text={t('common:loading')} />;
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Feedback variant="error">
          {t('somethingWentWrongLoadingCompanyDetailsPlease')}
        </Feedback>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4 pt-6">
        <div className="mx-auto flex w-full max-w-[600px] justify-center px-6">
          <div className="w-full">
            <p className="mb-2 px-4 text-left text-gray-600 dark:text-gray-400">
              {t('shareInformationAboutYourCompanyToHelp')}
            </p>
            {/* Company name */}
            <div className="border-lighter-gray bg-action-secondary mb-2 px-4 py-3">
              <span className={questionLabelClass}>{t('companyName')}</span>
              <input
                aria-label={t('companyName')}
                value={localCompanyMemory.companyName ?? ''}
                onChange={(e) =>
                  handleFieldChange('companyName', e.target.value)
                }
                placeholder={t('yourCompanyName')}
                className={`mt-2 ${inputClass}`}
                data-testid="companyName"
              />
            </div>

            {/* Company website */}
            <div className="border-lighter-gray bg-action-secondary mb-2 px-4 py-3">
              <span className={questionLabelClass}>{t('companyWebsite')}</span>
              <input
                aria-label={t('companyWebsite')}
                type="url"
                value={localCompanyMemory.website ?? ''}
                onChange={(e) => handleFieldChange('website', e.target.value)}
                placeholder="https://example.com"
                className={`mt-2 ${inputClass}`}
                data-testid="website"
              />
              {urlError && (
                <p className="mt-1 text-sm text-red-600">{urlError}</p>
              )}
            </div>

            {/* Other things to save in memory (company-wide) */}
            <div className="border-lighter-gray bg-action-secondary mb-2 px-4 py-3">
              <span className={questionLabelClass}>
                {t('otherThingsToSaveInMemory')}
              </span>
              <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                {t('companyWideContextThatAppliesToEveryone')}
              </p>
              <textarea
                aria-label={t('otherThingsToSaveInMemory')}
                value={localCompanyMemory.otherNotes ?? ''}
                onChange={(e) =>
                  handleFieldChange('otherNotes', e.target.value)
                }
                rows={4}
                className={textareaClass}
                data-testid="otherNotes"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 flex-shrink-0 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex w-full flex-col gap-2">
          {updateCompanyMemoryMutation.error ? (
            <Feedback variant="error">
              {t('failedToSavePleaseTryAgain')}
            </Feedback>
          ) : null}
          {updateCompanyMemoryMutation.isSuccess ? (
            <Feedback variant="success">{t('savedSuccessfully')}</Feedback>
          ) : null}
          <div className="flex w-full justify-end font-sans">
            <Button
              variant="primary"
              className="mx-0 mt-0 w-auto px-4 py-1.5 text-sm"
              data-testid="save-about-company"
              isLoading={updateCompanyMemoryMutation.isPending}
              onClick={handleSave}
              disabled={!!urlError}
            >
              {t('common:save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantMemoryComponent;
